import type { TipoMovimiento } from './tiposMovimiento';

/**
 * Catálogo de motivos de los movimientos manuales de stock (backend: motivos_movimiento_stock): todo registro manual
 * elige uno (obligatorio).
 */
export type SentidoMotivo = 'entrada' | 'salida';
export type ACargoDe = 'empresa' | 'proveedor' | 'mensajeria' | 'cliente';

export interface MotivoMovimiento {
  id: number;
  codigo: string;
  nombre: string;
  sentido: SentidoMotivo;
  tipo: TipoMovimiento;
  aCargoDe: ACargoDe | null;
  pideReferencia: boolean;
  /** Datos que pide al registrar (backend: DATOS_QUE_PIDE); faltan en respuestas viejas: no los pide. */
  pideVenta?: boolean;
  pidePublicacion?: boolean;
  pideContacto?: boolean;
  pideDetalle?: boolean;
  activo: boolean;
  orden: number;
}

/**
 * Datos que un motivo puede pedir al registrar el movimiento (se configuran en Mantenimiento; el backend los exige:
 * DATOS_QUE_PIDE en motivos-movimiento-stock.ts). Los que tienen `campo` son campos propios del formulario, que aparecen
 * sólo con un motivo que los pide; la referencia y el detalle están siempre.
 */
export const DATOS_QUE_PIDE = [
  { marca: 'pideReferencia', nombre: 'Referencia', casilla: 'Pide referencia (envío, venta, remito o reclamo)' },
  { marca: 'pideVenta', nombre: 'Número de venta', casilla: 'Pide número de venta', campo: 'numeroVenta', corto: 'Venta', ejemplo: 'Ej.: 2000001234567890' },
  {
    marca: 'pidePublicacion', nombre: 'Publicación de Mercado Libre', casilla: 'Pide publicación de Mercado Libre', campo: 'publicacion',
    corto: 'Publicación', ejemplo: 'Ej.: MLA1234567890',
  },
  {
    marca: 'pideContacto', nombre: 'Contacto del cliente', casilla: 'Pide contacto del cliente', campo: 'contacto', corto: 'Contacto',
    ejemplo: 'Nombre y teléfono, o usuario de Mercado Libre',
  },
  { marca: 'pideDetalle', nombre: 'Detalle', casilla: 'Pide detalle (qué pasó)' },
] as const;

export type MarcaPide = (typeof DATOS_QUE_PIDE)[number]['marca'];
/** Los campos propios del formulario (número de venta, publicación, contacto). */
export const CAMPOS_QUE_PIDE = DATOS_QUE_PIDE.filter((d): d is Extract<(typeof DATOS_QUE_PIDE)[number], { campo: string }> => 'campo' in d);
export type CampoPedido = (typeof CAMPOS_QUE_PIDE)[number]['campo'];

/**
 * Nombre en pantalla de lo manual sin motivo del catálogo (anterior a que fuera obligatorio). Backend:
 * ETIQUETA_SIN_MOTIVO (el informe lo usa con " — salidas" / " — entradas"). La clave interna del filtro sigue siendo
 * sinCatalogar.
 */
export const ETIQUETA_SIN_MOTIVO = 'Sin motivo (histórico)';

export const URL_MOTIVOS = '/api/v3/motivos-movimiento-stock';

export const A_CARGO_DE_LABEL: Record<ACargoDe, string> = {
  empresa: 'Empresa',
  proveedor: 'Proveedor',
  mensajeria: 'Mensajería',
  cliente: 'Cliente',
};
export const A_CARGO_DE: ACargoDe[] = ['empresa', 'proveedor', 'mensajeria', 'cliente'];

/** Etiqueta de "a cargo de" (null: sin asignar). */
export const etiquetaACargoDe = (a: string | null | undefined) => (a ? A_CARGO_DE_LABEL[a as ACargoDe] ?? a : 'Sin asignar');

export const SENTIDO_LABEL: Record<SentidoMotivo, string> = { salida: 'Salida', entrada: 'Entrada' };

/** Tipos que registra cada sentido (backend: TIPOS_MANUALES con TIPOS_ENTRADA / TIPOS_SALIDA). */
export const TIPOS_POR_SENTIDO: Record<SentidoMotivo, TipoMovimiento[]> = {
  salida: ['BAJA_ROTURA', 'BAJA_VENCIMIENTO', 'DEVOLUCION_PROVEEDOR', 'AJUSTE_NEGATIVO'],
  entrada: ['DEVOLUCION_CLIENTE', 'AJUSTE_POSITIVO'],
};

/** Lista del backend o nada (la respuesta puede no ser una lista mientras carga o si falla). */
export const comoLista = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
